import {
	type DiscoveredPrinter,
	formatLanAddress,
	type PrinterStatus,
	type PrintTransport,
	PrintTransportError,
	parseLanAddress,
} from "@luckymenu/staff-core";

/**
 * Print Bridge (`apps/print-bridge`), the PC companion on
 * `http://127.0.0.1:17307` (printing.md, Print Bridge; P5-T11). Browsers
 * cannot open raw sockets or the OS's USB and Bluetooth printers; Print
 * Bridge can. It keeps the printers' quirks (one queue per printer, 250 ms
 * between connections, retries only while nothing was sent, half-close).
 *
 * Two transports go through it: `lan` (a network printer, `host:port`) and
 * `bridge` (a printer, USB, serial or Bluetooth port of this PC, by the
 * bridge's own device ID).
 */

export const BRIDGE_URL = "http://127.0.0.1:17307";

/**
 * The oldest Print Bridge this app works with. 1.1.0 allows the LuckyMenu
 * origins, says when a failed job had partly reached the printer, and tells
 * a silent printer from an unreachable one.
 */
export const MIN_BRIDGE_VERSION = "1.1.0";

export type BridgeInfo = { version: string; os: string; hostname: string };

export type BridgeDetection =
	| { state: "running"; info: BridgeInfo }
	/** Installed but older than `MIN_BRIDGE_VERSION`: update it. */
	| { state: "outdated"; info: BridgeInfo }
	/** Not running, not installed, or the browser blocked local network access. */
	| { state: "missing" };

type Reply = { ok: boolean; error?: string; hint?: string; code?: string; partial?: boolean };

/** `-1`, `0` or `1`, comparing dotted version numbers. A `dev` build counts as newest. */
export function compareVersions(a: string, b: string): number {
	if (a === b) return 0;
	if (a === "dev") return 1;
	if (b === "dev") return -1;
	const pa = a.split(".").map((n) => Number.parseInt(n, 10) || 0);
	const pb = b.split(".").map((n) => Number.parseInt(n, 10) || 0);
	for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
		const d = (pa[i] ?? 0) - (pb[i] ?? 0);
		if (d !== 0) return d > 0 ? 1 : -1;
	}
	return 0;
}

/** Socket error codes that mean the printer was never reached. */
const UNREACHABLE = new Set([
	"ECONNREFUSED",
	"ETIMEDOUT",
	"EHOSTUNREACH",
	"ENETUNREACH",
	"EHOSTDOWN",
	"ENOENT",
	"ENODEV",
	"EACCES",
	"EBUSY",
]);

class BridgeReplyError extends Error {
	readonly status: number;
	readonly reply: Reply;
	constructor(status: number, reply: Reply) {
		super([reply.error, reply.hint].filter(Boolean).join(" — ") || `Print Bridge: HTTP ${status}`);
		this.status = status;
		this.reply = reply;
	}
}

/** Print Bridge itself did not answer: not running, or local access blocked. */
class BridgeDownError extends Error {}

/**
 * Set once Print Bridge answered in this browser (the guide or the add
 * printer wizard found it). Until then the transports do not call
 * 127.0.0.1 in the background: a PC that never installed the bridge is not
 * asked by Chrome for local network access, and makes no failing requests.
 */
const SEEN_KEY = "luckymenu.printBridge.seen";

function bridgeSeen(): boolean {
	try {
		return localStorage.getItem(SEEN_KEY) === "1";
	} catch {
		return false;
	}
}

function markBridgeSeen(): void {
	try {
		localStorage.setItem(SEEN_KEY, "1");
	} catch {
		// Private mode: the transports stay quiet until the next detection.
	}
}

/** A transport call: only once the bridge was seen in this browser. */
function transportCall(
	path: string,
	init: RequestInit | undefined,
	timeoutMs: number,
): Promise<Record<string, unknown>> {
	if (!bridgeSeen()) {
		return Promise.reject(new BridgeDownError("Print Bridge was never found on this computer"));
	}
	return call(path, init, timeoutMs);
}

async function call(
	path: string,
	init: RequestInit | undefined,
	timeoutMs: number,
): Promise<Record<string, unknown>> {
	let res: Response;
	try {
		// Chrome may ask the person once to let the site reach devices on this
		// computer (Local Network Access); the setup guide says to allow it.
		res = await fetch(BRIDGE_URL + path, { ...init, signal: AbortSignal.timeout(timeoutMs) });
	} catch (error) {
		if ((error as Error).name === "TimeoutError") {
			throw new PrintTransportError(
				"timeout",
				`No answer from Print Bridge after ${timeoutMs / 1000} s`,
			);
		}
		throw new BridgeDownError("Print Bridge is not running on this computer");
	}
	let body: Reply & Record<string, unknown>;
	try {
		body = (await res.json()) as Reply & Record<string, unknown>;
	} catch {
		body = { ok: false, error: `Print Bridge: HTTP ${res.status}` };
	}
	if (!res.ok || !body.ok) throw new BridgeReplyError(res.status, body);
	return body;
}

/** Whether Print Bridge runs on this computer, and whether it is new enough. */
export async function detectBridge(timeoutMs = 1500): Promise<BridgeDetection> {
	try {
		const reply = await call("/api/info", undefined, timeoutMs);
		if (reply.app !== "print-bridge") return { state: "missing" };
		markBridgeSeen();
		const info: BridgeInfo = {
			version: String(reply.version ?? "0"),
			os: String(reply.os ?? ""),
			hostname: String(reply.hostname ?? ""),
		};
		return compareVersions(info.version, MIN_BRIDGE_VERSION) < 0
			? { state: "outdated", info }
			: { state: "running", info };
	} catch {
		return { state: "missing" };
	}
}

/**
 * What a failed print through the bridge means for the job. The bridge down,
 * or a refusal before connecting: nothing was sent. `partial`, a timeout
 * while sending, or an unknown failure after the request went out: it may
 * have printed.
 */
function sendError(error: unknown): PrintTransportError {
	if (error instanceof PrintTransportError) {
		return error.code === "timeout"
			? new PrintTransportError("partial_write", error.message)
			: error;
	}
	if (error instanceof BridgeDownError)
		return new PrintTransportError("unreachable", error.message);
	if (error instanceof BridgeReplyError) {
		const { reply, status } = error;
		if (reply.partial) return new PrintTransportError("partial_write", error.message);
		// The bridge refused the request itself (policy, bad address): nothing sent.
		if (status >= 400 && status < 500) return new PrintTransportError("unreachable", error.message);
		if (reply.code && UNREACHABLE.has(reply.code)) {
			return new PrintTransportError("unreachable", error.message);
		}
		return new PrintTransportError("partial_write", error.message);
	}
	return new PrintTransportError("partial_write", String(error));
}

/** A status request: an unreachable printer throws; a silent one is null. */
function statusError(error: unknown): PrinterStatus | null {
	if (error instanceof BridgeDownError) {
		throw new PrintTransportError("unreachable", error.message);
	}
	if (error instanceof BridgeReplyError) {
		const { reply, status } = error;
		if (status === 501) return null; // a one-way spooler or CUPS queue
		if (status >= 400 && status < 500) throw new PrintTransportError("unreachable", error.message);
		if (reply.code && UNREACHABLE.has(reply.code)) {
			throw new PrintTransportError("unreachable", error.message);
		}
		return null; // connected, no answer
	}
	if (error instanceof PrintTransportError && error.code === "timeout") return null;
	throw error;
}

const octets = (data: Uint8Array): RequestInit => ({
	method: "POST",
	headers: { "content-type": "application/octet-stream" },
	body: data as unknown as BodyInit,
});

/** The job's time limit: the bridge's own (20 s plus 1 s per 32 KB) and a margin. */
const jobTimeout = (bytes: number) => 25_000 + Math.ceil(bytes / 32_768) * 1000;

function lanQuery(address: string): string {
	const lan = parseLanAddress(address);
	if (!lan)
		throw new PrintTransportError("unreachable", `Not a network printer address: ${address}`);
	return new URLSearchParams({ host: lan.host, port: String(lan.port) }).toString();
}

/** LAN printers through Print Bridge's relay: `POST /api/print/raw`, `GET /api/status`, `GET /api/scan`. */
export function bridgeLanTransport(): PrintTransport {
	return {
		connection: "lan",
		async send(address, data) {
			const query = lanQuery(address);
			try {
				await transportCall(`/api/print/raw?${query}`, octets(data), jobTimeout(data.length));
			} catch (error) {
				throw sendError(error);
			}
		},
		async status(address) {
			const query = lanQuery(address);
			try {
				const reply = await transportCall(`/api/status?${query}`, undefined, 8000);
				return reply.status as PrinterStatus;
			} catch (error) {
				return statusError(error);
			}
		},
		async discover() {
			let reply: Record<string, unknown>;
			try {
				reply = await transportCall("/api/scan?port=9100", undefined, 60_000);
			} catch {
				return [];
			}
			const printers = (reply.printers ?? []) as {
				host: string;
				port: number;
				status?: PrinterStatus;
			}[];
			return printers.map(
				(p): DiscoveredPrinter => ({
					connection: "lan",
					address: formatLanAddress({ host: p.host, port: p.port }),
					name: p.host,
					detail: p.status?.summary ?? null,
					rank: p.status ? 2 : 1,
					status: p.status ?? null,
				}),
			);
		},
	};
}

type BridgeDevice = {
	id: string;
	kind: "printer" | "usb" | "serial" | "bluetooth";
	name: string;
	detail?: string;
};

const KIND_RANK: Record<BridgeDevice["kind"], number> = {
	usb: 3,
	printer: 2,
	bluetooth: 2,
	serial: 1,
};

/** Printers, USB devices and serial or Bluetooth ports of this PC, through Print Bridge. */
export function bridgeDeviceTransport(): PrintTransport {
	return {
		connection: "bridge",
		async send(address, data) {
			const query = new URLSearchParams({ id: address }).toString();
			try {
				await transportCall(
					`/api/devices/print?${query}`,
					octets(data),
					jobTimeout(data.length) + 35_000,
				);
			} catch (error) {
				throw sendError(error);
			}
		},
		async status(address) {
			const query = new URLSearchParams({ id: address }).toString();
			try {
				const reply = await transportCall(`/api/devices/status?${query}`, undefined, 8000);
				return reply.status as PrinterStatus;
			} catch (error) {
				return statusError(error);
			}
		},
		async discover() {
			let reply: Record<string, unknown>;
			try {
				reply = await transportCall("/api/devices", undefined, 10_000);
			} catch {
				return [];
			}
			// The bridge lists likely receipt printers first; keep that order.
			const devices = (reply.devices ?? []) as BridgeDevice[];
			return devices.map(
				(d, i): DiscoveredPrinter => ({
					connection: "bridge",
					address: d.id,
					name: d.name,
					detail: d.detail ?? null,
					rank: KIND_RANK[d.kind] - i / 1000,
				}),
			);
		},
	};
}
